import { useEffect, useMemo, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Boxes, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Desglose de empaque POR CONTENEDOR: detecta los productos cuyo lote quedó
 * costeado por paquete (costo ≥ precio mínimo / packing "100U/PQT") y permite
 * aplicar el factor a todos de una vez (ventas previas = sub-paquete).
 */
export default function DesgloseContenedorDialog({ contenedor, open, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [factores, setFactores] = useState({});
  const [sel, setSel] = useState(new Set());
  const [precios, setPrecios] = useState("no");
  const [motivo, setMotivo] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [soloSospechosos, setSoloSospechosos] = useState(true);

  useEffect(() => {
    if (!open || !contenedor) return;
    setData(null); setPreview(null); setMotivo(""); setPrecios("no"); setSoloSospechosos(true);
    setLoading(true);
    api.get(`/productos/_/contenedores/${contenedor.id}/desglose-sugerido`)
      .then(({ data }) => {
        setData(data);
        const f = {}; const s = new Set();
        data.filas.forEach((r) => { if (r.factor_sugerido) f[r.producto_id] = String(r.factor_sugerido); if (r.sospechoso && r.factor_sugerido && !r.ya_desglosado) s.add(r.producto_id); });
        setFactores(f); setSel(s);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo analizar el contenedor"))
      .finally(() => setLoading(false));
  }, [open, contenedor]);

  const filas = useMemo(() => (data?.filas || []).filter((r) => !soloSospechosos || r.sospechoso), [data, soloSospechosos]);
  const seleccionados = Array.from(sel).filter((id) => Number(factores[id]) >= 2);

  const body = () => ({
    producto_ids: seleccionados, factores: Object.fromEntries(seleccionados.map((id) => [id, Number(factores[id])])),
    ajustar_precios: precios, guardar_factor_empaque: true, ventas_previas: "sub_paquete", motivo: motivo.trim() || null,
  });

  const calcular = async () => {
    if (!seleccionados.length) return toast.error("Selecciona al menos un producto con factor ≥ 2");
    setLoading(true);
    try { const { data } = await api.post(`/productos/_/desglosar-masivo/preview`, body()); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo calcular"); }
    finally { setLoading(false); }
  };

  const aplicar = async () => {
    if (motivo.trim().length < 5) return toast.error("Escribe el motivo (mínimo 5 caracteres)");
    setSaving(true);
    try {
      const { data } = await api.post(`/productos/_/desglosar-masivo`, body());
      toast.success(`Desglose aplicado a ${data.ok} producto(s)${data.con_error ? ` · ${data.con_error} con error` : ""}`);
      onDone?.(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aplicar"); }
    finally { setSaving(false); }
  };

  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); setPreview(null); };

  if (!contenedor) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="desglose-contenedor-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Boxes className="w-5 h-5 text-primary" /> Desglosar empaques · {contenedor.referencia}</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Detecta productos cuyo lote quedó costeado <b>por paquete</b> (el costo del lote supera el precio mínimo de venta, o el packing dice "100U/PQT", "10U/CAJITA"…) y los convierte a <b>costo por unidad</b>: cantidad ×N y costo ÷N en lotes, despachos y pedidos. Asume que las ventas se hicieron por unidad (sub-paquete). <b>El valor del inventario no se infla</b>; lo que baja es el costo por unidad vendida en Finanzas.
          </p>
        </DialogHeader>

        {loading && !data && <div className="py-8 text-center text-sm text-muted-foreground">Analizando lotes del contenedor…</div>}
        {data && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-3">
                <span className="font-semibold" data-testid="desglose-cont-resumen">{data.sospechosos} de {data.productos} productos parecen costeados por paquete</span>
                <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={soloSospechosos} onChange={(e) => setSoloSospechosos(e.target.checked)} className="accent-primary" data-testid="desglose-cont-solo-sospechosos" /> Solo sospechosos</label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Precios de venta</Label>
                <select value={precios} onChange={(e) => { setPrecios(e.target.value); setPreview(null); }} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="desglose-cont-precios">
                  <option value="no">No tocar (ya están por unidad)</option>
                  <option value="dividir">Dividir ÷N</option>
                </select>
              </div>
            </div>
            <div className="max-h-[42vh] overflow-auto rounded-md border border-border mt-2">
              <table className="w-full text-[11px]">
                <thead className="bg-muted/50 sticky top-0"><tr>
                  <th className="p-2"><input type="checkbox" checked={filas.length > 0 && filas.every((r) => sel.has(r.producto_id) || r.ya_desglosado)} onChange={(e) => { const n = new Set(sel); filas.forEach((r) => { if (r.ya_desglosado) return; e.target.checked ? n.add(r.producto_id) : n.delete(r.producto_id); }); setSel(n); setPreview(null); }} className="accent-primary" data-testid="desglose-cont-check-all" /></th>
                  <th className="text-left p-2">Producto</th><th className="text-left p-2">Packing</th><th className="text-right p-2">Costo lote</th><th className="text-right p-2">Precio mín</th><th className="text-right p-2">Stock</th><th className="text-right p-2">Factor ×N</th><th className="text-right p-2">Costo nuevo</th><th className="p-2">Estado</th>
                </tr></thead>
                <tbody>
                  {filas.map((r) => {
                    const f = Number(factores[r.producto_id]) || 0;
                    return (
                      <tr key={r.producto_id} className={`border-t border-border/60 tabular-nums ${r.ya_desglosado ? "opacity-50" : ""}`} data-testid={`desglose-cont-fila-${r.producto_id}`}>
                        <td className="p-2 text-center"><input type="checkbox" disabled={r.ya_desglosado} checked={sel.has(r.producto_id)} onChange={() => toggle(r.producto_id)} className="accent-primary" /></td>
                        <td className="p-2 truncate max-w-[240px]" title={r.nombre}>{r.nombre}{r.codigo && <span className="text-muted-foreground"> · {r.codigo}</span>}</td>
                        <td className="p-2 text-muted-foreground">{r.packing_unit || "—"}</td>
                        <td className={`p-2 text-right font-semibold ${r.costo_supera_precio ? "text-rose-600" : ""}`}>{formatCOP(r.costo_lote)}</td>
                        <td className="p-2 text-right">{formatCOP(r.precio_minimo)}</td>
                        <td className="p-2 text-right">{formatNumber(r.stock_actual)}</td>
                        <td className="p-2 text-right"><Input type="number" min="2" value={factores[r.producto_id] ?? ""} disabled={r.ya_desglosado} onChange={(e) => { setFactores({ ...factores, [r.producto_id]: e.target.value }); setPreview(null); }} className="h-7 w-20 text-right text-[11px] ml-auto" placeholder="N" data-testid={`desglose-cont-factor-${r.producto_id}`} /></td>
                        <td className="p-2 text-right">{f >= 2 ? <b className={r.costo_lote / f < r.precio_minimo ? "text-emerald-700" : "text-rose-600"}>{formatCOP(Math.round(r.costo_lote / f))}</b> : "—"}</td>
                        <td className="p-2">
                          {r.ya_desglosado ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> desglosado</span>
                            : r.costo_supera_precio ? <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">costo ≥ precio</span>
                            : r.sospechoso ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">packing por paquete</span> : <span className="text-muted-foreground">ok</span>}
                          {r.pedidos_abiertos > 0 && <span className="ml-1 text-muted-foreground">· {r.pedidos_abiertos} ped. abiertos</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {filas.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">Ningún producto de este contenedor parece costeado por paquete.</td></tr>}
                </tbody>
              </table>
            </div>

            {!preview && (
              <Button onClick={calcular} disabled={loading || !seleccionados.length} variant="outline" className="mt-3 h-10" data-testid="desglose-cont-calcular-btn">
                {loading ? "Calculando..." : `Ver qué cambiaría (${seleccionados.length} productos)`}
              </Button>
            )}
            {preview && (
              <div className="mt-3 space-y-2" data-testid="desglose-cont-preview">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <Kpi label="Productos" value={preview.ok} sub={preview.con_error ? `${preview.con_error} con error` : "sin errores"} />
                  <Kpi label="Lotes" value={preview.resultados.reduce((a, r) => a + r.lotes, 0)} />
                  <Kpi label="Despachos re-expresados" value={preview.resultados.reduce((a, r) => a + r.despachos_a_reexpresar, 0)} />
                  <Kpi label="Pedidos con costo corregido" value={preview.resultados.reduce((a, r) => a + (r.pedidos_snapshot_a_corregir || 0), 0)} accent />
                </div>
                {preview.errores.length > 0 && (
                  <div className="text-[11px] text-rose-700 bg-rose-50 dark:bg-rose-900/20 rounded-md p-2 space-y-0.5" data-testid="desglose-cont-errores">
                    {preview.errores.map((er) => <div key={er.producto_id}>· {er.error}</div>)}
                  </div>
                )}
                <div className="max-h-40 overflow-auto rounded-md border border-border">
                  <table className="w-full text-[11px]"><thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-1.5">Producto</th><th className="text-right p-1.5">×N</th><th className="text-right p-1.5">Stock</th><th className="text-right p-1.5">Costo ficha</th><th className="text-right p-1.5">Precio mín</th></tr></thead>
                    <tbody>{preview.resultados.map((r) => (
                      <tr key={r.producto_id} className="border-t border-border/60 tabular-nums">
                        <td className="p-1.5 truncate max-w-[260px]">{r.nombre}</td><td className="p-1.5 text-right">×{r.factor}</td>
                        <td className="p-1.5 text-right"><span className="line-through text-muted-foreground mr-1">{formatNumber(r.stock_viejo)}</span>{formatNumber(r.stock_nuevo)}</td>
                        <td className="p-1.5 text-right"><span className="line-through text-muted-foreground mr-1">{formatCOP(r.costo_ficha_viejo || 0)}</span>{formatCOP(r.costo_ficha_nuevo || 0)}{r.costo_ficha_ya_unitario && <span className="text-muted-foreground"> (ya unitario)</span>}</td>
                        <td className="p-1.5 text-right"><span className="line-through text-muted-foreground mr-1">{formatCOP(r.precio_minimo_viejo || 0)}</span>{formatCOP(r.precio_minimo_nuevo || 0)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                <div>
                  <Label>Motivo *</Label>
                  <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: contenedor cargado con costo por paquete; vendemos por unidad" className="h-10 mt-1" data-testid="desglose-cont-motivo" />
                </div>
                <div className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Se guarda el factor en cada producto (próximos contenedores se convierten solos). Queda en auditoría. Después revisa Finanzas → Auditoría de costos.
                </div>
              </div>
            )}
          </>
        )}
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="desglose-cont-cancelar-btn">Cancelar</Button>
          {preview && preview.ok > 0 && (
            <Button onClick={aplicar} disabled={saving} className="bg-primary" data-testid="desglose-cont-aplicar-btn">{saving ? "Aplicando..." : `Aplicar desglose a ${preview.ok} productos`}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className={`rounded-lg border p-2 ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-muted/20"}`}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
